import { z } from "zod";
import { PlanAudienceSchema } from "./audience";

/** A plan written before audiences went to Meta: who it is for, in words only. */
const WordsAudienceSchema = z.object({ who: z.string(), size: z.string(), size_basis: z.string(), where: z.string() });

/**
 * The plan the agent writes for a goal (the save_plan tool's input). Ported from the agent spike's
 * contract so planning behaves the same; the only change is that `starts[].needs` is open: ids are
 * checked against the readiness registry the run was given, not a fixed list.
 */
/** One call the plan made that the owner might have made differently. */
const PlanChoiceSchema = z.object({
  choice: z.string().describe("What the plan does, in one plain line"),
  why: z.string().describe("Why, in one sentence"),
});

/** The calls a new plan must list: the owner reads them as "What it decided" and says "Not right" to any. */
export const PlanChoicesSchema = z
  .array(PlanChoiceSchema)
  .min(1)
  .max(5)
  .describe(
    "The two or three calls you made that the owner might have made differently, and any part of the playbook that could not work. The plan already follows each one. Never set-up (accounts, mailboxes, domains, keys, connections): that goes in needs_from_you",
  );

export const PlanSchema = z.object({
  summary: z.string().describe("Two or three sentences: the direction, in plain words"),
  goal: z.object({
    statement: z.string(),
    result: z.string().describe("What counts as one result"),
    deadline: z.string(),
    budget_usd: z.number(),
    max_cost_per_result_usd: z.number(),
    pace_needed: z.string(),
    assumptions: z.array(z.string()).default([]),
  }),
  lessons_used: z
    .array(z.object({ id: z.string(), how: z.string().describe("Used as a fact, or re-tested, and why") }))
    .min(1)
    .describe("Every lesson the plan relies on or re-tests"),
  findings: z
    .array(z.object({ finding: z.string(), source: z.string().describe("Link or tool result it came from") }))
    .min(3)
    .describe("What the research found, one fact per item, each with its source"),
  audience: z
    .union([PlanAudienceSchema, WordsAudienceSchema])
    .optional()
    .describe("Who sees the Meta ads, every place and starting point with the id find_targeting returned; leave out when the goal has no Meta ads"),
  meta_opens: z
    .object({
      opens: z.enum(["metaform", "site"]).describe("metaform: Meta's own form inside Facebook and Instagram; site: a page on the business's website"),
      questions: z
        .array(z.object({ q: z.string().max(150), answers: z.array(z.string().max(80)).max(10).describe("Leave empty for a typed answer") }))
        .max(3)
        .optional()
        .describe("For metaform: the qualifying question first, then at most two more"),
      why: z.string().describe("One sentence on the pick"),
    })
    .optional()
    .describe("Leave out when the goal has no Meta ads"),
  meta_events: z
    .object({
      counts: z.string().describe("The dataset event the goal counts as its result, by its exact Meta name from list_events (or a standard name it should send)"),
      learns_from: z.string().describe("The event Meta learns from, by its Meta name: the counted one when it comes about 50 times a week, else the step before; never PageView or ViewContent"),
      why: z.string().describe("One sentence on the pick"),
    })
    .optional()
    .describe("Leave out when the goal has no Meta ads"),
  angles: z
    .array(
      z.object({
        rank: z.number().int(),
        name: z.string(),
        message: z.string(),
        why: z.string(),
        evidence: z.array(z.string()).default([]),
        risk: z.string(),
        hook: z.string().describe("One example line an ad or email could open with"),
      }),
    )
    .min(2)
    .max(3),
  tests: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        hypothesis: z.string(),
        variants: z
          .array(z.string())
          .describe('Each one as "KEY: what it is", with a short key unique in this test (a letter or a word). Ads and emails name their variant by that key'),
        metric: z.string(),
        min_data: z.string(),
        budget_usd: z.number(),
        days: z.number().int(),
        stop_early_if: z.string(),
      }),
    )
    .min(1)
    .max(4),
  budget: z.object({
    split: z.array(z.object({ item: z.string(), usd: z.number() })),
    cost_per_result: z.string().describe("A range, with the reasoning"),
    expected_results: z.string(),
    meta_daily_usd: z.number().positive().optional().describe("Where Meta's daily amount starts, in dollars; the reason goes in cost_per_result. Leave out when the goal has no Meta ads"),
    reachable: z.string().describe("Honest view: can the goal be reached, and what would change that"),
  }),
  timeline: z.array(z.object({ when: z.string(), what: z.string() })),
  tools: z.object({ will_use: z.array(z.string()), missing: z.array(z.string()).default([]) }),
  autonomy: z
    .array(z.object({ action: z.string(), mode: z.enum(["ask", "within_limits", "on_its_own"]), why: z.string() }))
    .min(1)
    .describe("What the agent does alone, within limits, or only after asking"),
  risks: z.array(z.object({ risk: z.string(), signal: z.string(), response: z.string() })),
  change_course_if: z.array(z.string()).min(1).describe("Signals that change the plan, each with what changes"),
  needs_from_you: z.array(z.string()).min(1).describe("Everything the person has to provide"),
  headline: z
    .object({
      pace: z.string().describe("Results needed per week, a few words, e.g. '3.5 a week'"),
      cost: z.string().describe("Cost per result as a short range, e.g. '$30 to $60'"),
      expected: z.string().describe("Results expected as a short range, e.g. '30 to 50'"),
    })
    .describe("Three short numbers for the top of the review"),
  bet: z.string().describe("One sentence: the single thing that must go right"),
  // Why: plans saved before the plan listed its calls have none; save_plan requires them (PlanChoicesSchema).
  choices: z.array(PlanChoiceSchema).max(5).default([]),
  starts: z
    .array(
      z.object({
        what: z.string(),
        when: z.string(),
        needs: z
          .array(z.string())
          .describe("Ids from the readiness list you were given: what must be ready first; empty if it can start right after approval"),
      }),
    )
    .min(2)
    .describe("Each part of the plan: when it starts and what it needs. Channels never wait for each other."),
  playbook_followed: z
    .array(z.object({ rule: z.string().describe("The playbook rule, briefly"), how: z.string().describe("How it shaped this plan") }))
    .min(3)
    .describe("Each playbook rule that shaped the plan"),
  conflicts: z
    .array(z.object({ rule: z.string(), evidence: z.string(), suggestion: z.string() }))
    .default([])
    .describe("Playbook rules you followed but disagree with, and why"),
});
export type Plan = z.infer<typeof PlanSchema>;

/** A test variant's key and words: "A: recordings into ads" is key "A". A variant with no key is its own key. */
export function splitVariant(variant: string): { key: string; text: string } {
  const m = variant.match(/^([^:\n]{1,60}):\s+([\s\S]+)$/);
  return m ? { key: m[1].trim(), text: m[2].trim() } : { key: variant.trim(), text: "" };
}

/** The owner's answers on the review: one per decision (and per conflict), plus any typed fix. */
/** The plan's audience when it is one Meta can take, not a plan from before audiences. */
export const metaAudienceOf = (p: Plan) => (p.audience && "places" in p.audience ? p.audience : null);

/** One of the plan's ad ideas: its key, the variant as the plan words it, and what it tests. */
export interface PlanIdea {
  key: string;
  text: string;
  why: string;
}

/**
 * The plan's ideas for the ads: the variants of its first Meta test (any first test when none names
 * Meta), each with the reason its angle gives, else what the test sets out to show.
 */
export function planIdeas(p: Plan): PlanIdea[] {
  const test = p.tests.find((t) => /meta|ad/i.test(t.name)) ?? p.tests[0];
  if (!test) return [];
  return test.variants.map((v) => {
    const { key } = splitVariant(v);
    const angle = p.angles.find((a) => v.toLowerCase().includes(a.name.toLowerCase()));
    return { key, text: v.trim(), why: angle?.why ?? test.hypothesis };
  });
}
